import { AxiosInstance } from "axios";
import AbstractModel from "../types/negocio/AbstractModel";

export default abstract class BaseService<T extends AbstractModel> {

    constructor(
        protected readonly route: string,
        protected readonly api: AxiosInstance) { }

    /**
     *  Lista todas as entidades
     *  @description Este método busca todas as entidades do tipo T na API.
     *  Ele faz uma requisição GET para a rota definida e retorna uma lista de entidades.
     *  É utilizado para obter todos os registros de uma entidade específica.
     * @example
     * const usuarios = await usuarioService.listarTodos();
     * @throws Erro se a requisição falhar
     * @throws AxiosError se houver um erro na requisição
     * @throws Error se ocorrer um erro inesperado
     * @returns Promise com uma lista de todas as entidades
     */
    async listarTodos(): Promise<T[]> {
        const response = await this.api.get(`/api/${this.route}`);
        return response.data;
    }

    /**
     * Busca uma entidade pelo ID
     * @description Este método busca uma entidade específica pelo seu ID.
     * Ele faz uma requisição GET para a rota definida com o ID da entidade e retorna a entidade encontrada.
     * É utilizado para obter um registro específico de uma entidade.
     * @example
     * const usuario = await usuarioService.buscarPorId(1);
     * @throws Erro se a requisição falhar
     * @param id ID da entidade a ser buscada
     * @returns Promise com a entidade encontrada
     */
    async buscarPorId(id: string): Promise<T> {
        const response = await this.api.get(`/api/${this.route}/${id}`);
        return response.data;
    }

    /**
     * Cria uma nova entidade
     * @description Este método cria uma nova entidade do tipo T.
     * Ele faz uma requisição POST para a rota definida com os dados da entidade e retorna a entidade criada.
     * É utilizado para adicionar um novo registro de uma entidade específica.     
     * @param data Dados da entidade a ser criada
     * @returns Promise com a entidade criada
     */
    async criar(data: T): Promise<T> {
        const response = await this.api.post(`/api/${this.route}`, data);
        return response.data;
    }

    /**
     * Atualiza uma entidade pelo ID
     * @param id ID da entidade a ser atualizada
     * @param data Novos dados da entidade
     * @returns Promise com a entidade atualizada
     */
    async atualizar(data: T): Promise<T> {
        const response = await this.api.put(`/api/${this.route}`, data);
        return response.data;
    }

    /**
     * Deleta uma entidade pelo ID
     * @param id ID da entidade a ser deletada
     */
    async deletar(id: string): Promise<void> {
        await this.api.delete(`/api/${this.route}/${id}`);
    }

    /**
     * Salva uma nova entidade
     * @param entidade Dados da entidade a ser salva
     * @returns Promise com a entidade salva incluindo o GUID gerado
     */
    async save(entidade: T): Promise<T> {
        const response = entidade.guid
            ? await this.api.put(`/api/${this.route}`, entidade)
            : await this.api.post(`/api/${this.route}`, entidade);
        return response.data;
    }

    /**
     * Busca uma entidade pelo GUID
     * @param guid GUID da entidade a ser buscada
     * @returns Promise com a entidade encontrada
     */
    salvarTodos(entidades: T[]): Promise<T[]> {
        return Promise.all(entidades.map(entidade => this.save(entidade)));
    }

}